import {Dispatch, FC, SetStateAction, useEffect, useMemo, useRef, useState} from 'react'
import axios from 'axios'
import {
  API_URL,
  isNotEmpty,
  isPOS,
  numberWithCommas,
  onTSP,
} from '../../../../../../_metronic/helpers'
import {CustomInputs} from '../../../../../../_metronic/helpers/components/CustomInputs'
import {
  SearchSelect,
  SearchSelectOption,
} from '../../../../../../_metronic/helpers/components/SearchSelect'
import {ListLoading} from '../../../../../../_metronic/helpers/components/ListLoading'
import {Alert} from '../../../../../../_metronic/helpers/components/Alert'
import {Invoice, InvoiceTypeList} from '../core/_models'
import {useListView} from '../core/ListViewProvider'
import {getInvoiceErrorAppLogId, getInvoiceErrorMessage} from '../core/invoiceError'
import {
  getReferencedInvoiceMinimumDate,
  REFERENCED_INVOICE_DATE_HINT,
} from '../core/invoiceReferenceDate'
import {ICompanyTaxid} from '../../../taxpayer-management/taxpayers-setting/_models'
import {isInvoiceBuyerRequired, optionalBuyerHint} from '../core/invoiceBuyerRequirement'
import {INVOICE_ENTRY_TYPE, resolveInvoiceAutoSendControlValue} from '../core/invoiceAutoSend'
import {useInvoiceAutoSendDefault} from '../core/useInvoiceAutoSendDefault'
import {useAuth} from '../../../../auth'
import {useUnsavedWork} from '../../../shared/business-switcher/unsavedWork'
import {WebInvoiceErrorSupportAction} from '../components/WebInvoiceErrorSupportAction'
import {FieldHelpLabel} from '../../../../../../_metronic/helpers/components/FieldHelp'
import {
  InvoiceArticle9IssueDateAccessory,
  InvoiceArticle9RegistrationField,
} from '../components/InvoiceArticle9Fields'
import {
  article9UnixMillisecondsToDateTime,
  buildInvoiceArticle9Request,
  getInvoiceArticle9DraftError,
} from '../core/invoiceArticle9'
import {
  getAdvancedFormTemplate,
  getInsuranceIdsError,
  getInvoicePosPayments,
  isInsuranceTemplate,
  parsePosTransactionAmount,
} from './advancedCostFormPolicy'

const INVOICE_URL = `${API_URL}/invoices`

type Props = {
  invoice: Invoice | null | undefined
  invoiceType: number
  invoiceTemplate: number
  title: {
    title: string
    setTitle: Dispatch<SetStateAction<string>>
  }
}

const getNumberValue = (value: string | number | undefined | null) => {
  const normalized = String(value ?? '').replace(/,/g, '')
  const numberValue = Number(normalized || 0)
  return Number.isFinite(numberValue) ? numberValue : 0
}

/** «الف، ب و ج» */
const joinPersianList = (items: string[]) =>
  items.length <= 1
    ? items.join('')
    : `${items.slice(0, -1).join('، ')} و ${items[items.length - 1]}`

const InvoiceAdvancedCostFormBody: FC<Props> = ({invoice, invoiceType, invoiceTemplate, title}) => {
  const {currentUser} = useAuth()
  useUnsavedWork('فرم صورتحساب')
  const {defaultAutoSend} = useInvoiceAutoSendDefault(
    INVOICE_ENTRY_TYPE.webFormAdvancedCostPrice,
    currentUser?.cpi
  )
  const {itemIdForUpdate, variableD} = useListView()
  const minimumReferenceDate = getReferencedInvoiceMinimumDate(invoice, variableD)
  const [first, setFirst] = useState(true)
  const [serverNo, setServerNo] = useState('')
  const [no, setNo] = useState('')
  const [date, setDate] = useState('')
  const [isArticle9, setIsArticle9] = useState(invoice?.insr === 1)
  const [article9RegistrationDateTime, setArticle9RegistrationDateTime] = useState(() =>
    article9UnixMillisecondsToDateTime(invoice?.indati2m)
  )
  const [price, setPrice] = useState('')
  const [buyer, setBuyer] = useState<SearchSelectOption | undefined>()
  const [buyerDescription, setBuyerDescription] = useState('')
  const [posTransaction, setPosTransaction] = useState<SearchSelectOption | undefined>()
  const [now, setNow] = useState('')
  const [currentStep, setCurrentStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const submittingRef = useRef(false)
  const [errorModal, setErrorModal] = useState('')
  const [errorAppLogId, setErrorAppLogId] = useState<number>()
  const [fiscalMemories, setFiscalMemories] = useState<ICompanyTaxid[]>([])
  const [fiscalMemoryId, setFiscalMemoryId] = useState<number | undefined>(invoice?.fmi)
  const [isProductionEnvironment, setIsProductionEnvironment] = useState(true)
  const [isSend, setIsSend] = useState(false)
  const [autoSendTouched, setAutoSendTouched] = useState(false)
  const [insPolicyNo, setInsPolicyNo] = useState('')
  const [extInsPolicyNo, setExtInsPolicyNo] = useState('')
  // The server's answers to an edit of a sent invoice, handled as the full form handles them.
  const [modifyModal, setModifyModal] = useState(false)
  const [ebtalNewModal, setEbtalNewModal] = useState(false)
  const isEdit = !!invoice?.id
  const template = getAdvancedFormTemplate(invoice, invoiceTemplate)
  const posPayments = isEdit ? getInvoicePosPayments(invoice) : []
  const selectedPosAmount = parsePosTransactionAmount(posTransaction?.label)

  const displayedIsSend = resolveInvoiceAutoSendControlValue(
    invoice?.id ? undefined : defaultAutoSend,
    isSend,
    autoSendTouched
  )

  const setIsSendFromControl: Dispatch<SetStateAction<boolean>> = (value) => {
    setAutoSendTouched(true)
    setIsSend(value)
  }

  const priceNumber = useMemo(() => getNumberValue(price), [price])
  const isTypeOne = invoiceType == 1
  const isBuyerRequired = isInvoiceBuyerRequired(invoiceType, template)
  // A type-2 invoice shows its buyer only when it already has one, so an edit keeps it visibly.
  const showBuyerFields = isTypeOne || (isEdit && !!invoice?.buci)
  // An edit keeps the invoice's buyer, buyer description, insurance ids and fiscal memory: the
  // server takes them from the invoice, so here they are shown read-only and not checked again.
  const keptOnEditFields = [
    ...(showBuyerFields ? ['خریدار', 'توضیحات خریدار'] : []),
    ...(isInsuranceTemplate(template) ? ['شناسه‌های بیمه'] : []),
    'حافظهٔ مالیاتی',
  ]
  const currentCompany = currentUser?.cpl?.find((company) => company.id === currentUser?.cpi)
  // The form selector offers the full form only outside the POS app and when the business has it.
  const fullFormOffered = !isPOS && (!currentCompany?.frm?.length || currentCompany.frm.includes(0))
  const insuranceError = isEdit ? '' : getInsuranceIdsError(template, insPolicyNo, extInsPolicyNo)
  const article9DraftError = getInvoiceArticle9DraftError(
    isArticle9,
    invoiceType,
    article9RegistrationDateTime,
    date,
    now
  )
  const article9Request = buildInvoiceArticle9Request(
    isArticle9,
    invoiceType,
    article9RegistrationDateTime
  )
  const canContinue =
    isNotEmpty(no) &&
    isNotEmpty(date) &&
    priceNumber > 0 &&
    (isEdit || !!fiscalMemoryId) &&
    (isEdit || !isBuyerRequired || !!buyer) &&
    !article9DraftError &&
    !insuranceError

  const setFormTitle = () => {
    const selectedType = InvoiceTypeList.find((x) => x.id == invoiceType)
    let invoiceName = selectedType?.name || ''
    if (selectedType?.template) {
      invoiceName += ' - ' + selectedType.template.find((x) => x.id == template)?.name
    }

    if (invoice && invoice.id) {
      title.setTitle(`ویرایش فرم قیمت تمام شده پیشرفته ${no} ${date}`)
      return
    }

    title.setTitle(`صدور فرم قیمت تمام شده پیشرفته ${invoiceName}`)
  }

  useEffect(() => {
    if (first) {
      if (invoice && invoice.id) {
        setNo(invoice.no || '')
        setDate(invoice.idt || '')
        setIsArticle9(invoice.insr === 1)
        setArticle9RegistrationDateTime(article9UnixMillisecondsToDateTime(invoice.indati2m))
        setPrice((invoice.ntp || '').toString())
        if (invoice.buci) {
          setBuyer({value: invoice.buci, label: invoice.bun || invoice.buc || `${invoice.buci}`})
        }
        setBuyerDescription(invoice.bud || '')
        setInsPolicyNo(invoice.ipno || '')
        setExtInsPolicyNo(invoice.eipno || '')
      } else {
        axios.get(`${INVOICE_URL}/checkNo/`).then((response) => {
          setServerNo(response.data)
          setNo(response.data)
        })
      }

      axios.get(`${API_URL}/taxpayer/getdatetime/`).then((response) => {
        setNow(response.data)
      })

      axios
        .get(`${API_URL}/taxpayer/fiscalIds`)
        .then(({data}) => {
          const tokens = (data || []) as ICompanyTaxid[]
          setFiscalMemories(tokens)
          const boundToken = tokens.find((token) => token.id === invoice?.fmi)
          const targetEnvironment = boundToken ? boundToken.isActive !== false : true
          setIsProductionEnvironment(targetEnvironment)
          // An edit shows the invoice's own fiscal memory, which the server keeps.
          if (!invoice?.id && !fiscalMemoryId) {
            const enabled = tokens.filter(
              (token) =>
                token.isEnabled !== false && (token.isActive !== false) === targetEnvironment
            )
            const selected =
              enabled.find((token) => token.isDefault) ||
              (enabled.length === 1 ? enabled[0] : undefined)
            setFiscalMemoryId(selected?.id)
          }
        })
        .catch(() => setErrorModal('دریافت شناسه‌های حافظه مالیاتی ناموفق بود.'))

      setFirst(false)
    }
  }, [])

  useEffect(() => {
    if (!(invoice && invoice.id) && now) {
      setDate(now)
    }
  }, [now])

  useEffect(() => {
    if (invoiceType === 3 && isArticle9) {
      setIsArticle9(false)
    }
  }, [invoiceType, isArticle9])

  useEffect(() => {
    setFormTitle()
  }, [invoiceType, template, no, date])

  useEffect(() => {
    if (no != serverNo) {
      setServerNo('')
    }
  }, [no])

  // The transaction was picked for this day and this amount; either change asks for a new pick.
  useEffect(() => {
    setPosTransaction(undefined)
  }, [date, priceNumber])

  const save = (confirm = false) => {
    if (loading || submittingRef.current) return

    if (article9DraftError) {
      setErrorModal(article9DraftError)
      return
    }
    if (insuranceError) {
      setErrorModal(insuranceError)
      return
    }
    submittingRef.current = true
    setLoading(true)

    axios
      .post(`${INVOICE_URL}/advancedCostPrice/${itemIdForUpdate || ''}`, {
        no,
        date,
        price: priceNumber,
        type: invoiceType,
        template,
        buyer: buyer?.value?.toString() || '',
        buyerDescription,
        insPolicyNo: isInsuranceTemplate(template) ? insPolicyNo.trim() : undefined,
        extInsPolicyNo:
          isInsuranceTemplate(template) && extInsPolicyNo.trim()
            ? extInsPolicyNo.trim()
            : undefined,
        onlinePosTransactionId: posTransaction?.value ? Number(posTransaction.value) : null,
        fiscalMemoryId,
        isProductionEnvironment,
        isArticle9: article9Request.isArticle9,
        indati2m: article9Request.indati2m,
        isSend: !invoice?.id && !autoSendTouched ? defaultAutoSend : isSend,
        action: variableD,
        execute: false,
        confirm,
        save: false,
      })
      .then((response) => {
        if (isPOS) {
          window.location.href = '/invoicePrint/' + response.data
        } else {
          window.location.href = '/apps/invoice-management'
        }
      })
      .catch((error) => {
        submittingRef.current = false
        setLoading(false)
        const status = Number(error?.response?.status)
        if (status === 426) {
          setEbtalNewModal(true)
        } else if (status === 415) {
          setModifyModal(true)
        } else {
          setErrorAppLogId(getInvoiceErrorAppLogId(error))
          setErrorModal(
            getInvoiceErrorMessage(error, 'صدور صورتحساب ناموفق بود. لطفاً دوباره تلاش کنید.')
          )
        }
      })
  }

  return (
    <>
      <div className='row justify-content-center'>
        <div className='col-xl-7 col-lg-9 col-12'>
          <div className='d-flex align-items-center justify-content-center gap-3 mb-8'>
            <span className={`badge ${currentStep == 0 ? 'badge-primary' : 'badge-light'}`}>
              <FieldHelpLabel label='اطلاعات' helpKey='section.header' />
            </span>
            <span className='text-gray-400'>/</span>
            <span className={`badge ${currentStep == 1 ? 'badge-primary' : 'badge-light'}`}>
              <FieldHelpLabel label='تأیید' helpKey='section.confirmation' />
            </span>
          </div>

          {currentStep == 0 && (
            <div className='row'>
              {isEdit && (
                <div className='col-12 mb-5'>
                  <div className='alert alert-light-info mb-0'>
                    در ویرایش با این فرم، {joinPersianList(keptOnEditFields)} صورتحساب تغییر نمی‌کند
                    {fullFormOffered
                      ? '؛ برای تغییر آن‌ها صورتحساب را با «فرم کامل» ویرایش کنید.'
                      : '.'}
                  </div>
                </div>
              )}
              <div className='col-md-5 col-12'>
                <CustomInputs
                  type='text'
                  label='شماره صورتحساب'
                  isRequired
                  stateValue={{value: no, setValue: setNo}}
                />
              </div>
              <div className='col-md-7 col-12'>
                <CustomInputs
                  type='datepicker'
                  label={<>تاریخ و زمان صدور صورتحساب</>}
                  labelAccessory={
                    <InvoiceArticle9IssueDateAccessory
                      invoiceType={invoiceType}
                      issueDateTime={date}
                      isArticle9={isArticle9}
                      setIsArticle9={setIsArticle9}
                    />
                  }
                  timePicker
                  isRequired
                  limitFromDate={minimumReferenceDate}
                  limitToDate={now}
                  stateValue={{value: date, setValue: setDate}}
                  hint={minimumReferenceDate ? REFERENCED_INVOICE_DATE_HINT : undefined}
                />
              </div>
              {(isArticle9 || invoiceType === 3) && (
                <div className='col-md-7 col-12 offset-md-5 mt-5'>
                  <InvoiceArticle9RegistrationField
                    invoiceType={invoiceType}
                    issueDateTime={date}
                    now={now}
                    isArticle9={isArticle9}
                    setIsArticle9={setIsArticle9}
                    registrationDateTime={article9RegistrationDateTime}
                    setRegistrationDateTime={setArticle9RegistrationDateTime}
                  />
                </div>
              )}
              <div className='col-12 mt-5'>
                <CustomInputs
                  type='currency'
                  label='قیمت تمام شده'
                  isRequired
                  stateValue={{value: price, setValue: setPrice}}
                />
                {/* Outside the input's hint, which keeps its first value until the price changes. */}
                {selectedPosAmount !== undefined && (
                  <div
                    className={`fs-7 mt-2 ${
                      selectedPosAmount > priceNumber ? 'text-danger' : 'text-gray-600'
                    }`}
                    role='status'
                  >
                    مبلغ تراکنش کارت‌خوان انتخاب‌شده: {numberWithCommas(selectedPosAmount)} ریال
                    {selectedPosAmount > priceNumber &&
                      '؛ این مبلغ از قیمت تمام شده بیشتر است، قیمت را اصلاح یا تراکنش دیگری انتخاب کنید.'}
                  </div>
                )}
              </div>
              {isInsuranceTemplate(template) && (
                <>
                  <div className='col-md-6 col-12 mt-5'>
                    <CustomInputs
                      type='text'
                      numberAsText
                      digitsOnly
                      isRequired
                      isDisabled={isEdit}
                      label='شناسه یکتای بیمه‌نامه'
                      max={12}
                      stateValue={{value: insPolicyNo, setValue: setInsPolicyNo}}
                      hint={
                        <span className='text-gray-600 fs-8'>
                          برای خدمات بیمه الزامی است؛ ۹ تا ۱۲ رقم.
                        </span>
                      }
                    />
                  </div>
                  <div className='col-md-6 col-12 mt-5'>
                    <CustomInputs
                      type='text'
                      numberAsText
                      digitsOnly
                      isDisabled={isEdit}
                      label='شناسه یکتای الحاقیه'
                      max={12}
                      stateValue={{value: extInsPolicyNo, setValue: setExtInsPolicyNo}}
                      hint={
                        <span className='text-gray-600 fs-8'>
                          اختیاری است؛ در صورت ورود باید ۹ تا ۱۲ رقم باشد.
                        </span>
                      }
                    />
                  </div>
                </>
              )}
              {showBuyerFields && (
                <div className='col-12 mt-5'>
                  <SearchSelect
                    section='buyer'
                    label={`خریدار صورتحساب${isBuyerRequired ? '' : ' (اختیاری)'}`}
                    placeholder='نام، شناسه ملی یا کد اقتصادی خریدار را جستجو کنید...'
                    isRequired={isBuyerRequired}
                    isDisabled={isEdit}
                    stateValue={{value: buyer, setValue: setBuyer}}
                    hint={
                      isEdit
                        ? undefined
                        : isBuyerRequired
                        ? 'در نوع صورتحساب ۱، خریدار باید قبل از صدور انتخاب شود.'
                        : optionalBuyerHint(template)
                    }
                  />
                </div>
              )}
              {showBuyerFields && (
                <div className='col-12 mt-5'>
                  <CustomInputs
                    type='textarea'
                    label='توضیحات خریدار'
                    isDisabled={isEdit}
                    stateValue={{value: buyerDescription, setValue: setBuyerDescription}}
                  />
                </div>
              )}
              <div className='col-12 mt-5'>
                <label className='form-label required'>
                  <FieldHelpLabel label='محیط صورتحساب' helpKey='ui.environment' isRequired />
                </label>
                <select
                  className='form-select mb-4'
                  value={isProductionEnvironment ? 'production' : 'sandbox'}
                  disabled={!!invoice?.id}
                  onChange={(event) => {
                    const production = event.target.value === 'production'
                    setIsProductionEnvironment(production)
                    const enabled = fiscalMemories.filter(
                      (token) =>
                        token.isEnabled !== false && (token.isActive !== false) === production
                    )
                    const selected =
                      enabled.find((token) => token.isDefault) ||
                      (enabled.length === 1 ? enabled[0] : undefined)
                    setFiscalMemoryId(selected?.id)
                  }}
                >
                  <option value='production'>محیط عملیاتی</option>
                  <option value='sandbox'>محیط سندباکس</option>
                </select>
                <label className='form-label required'>
                  <FieldHelpLabel
                    label='شناسه حافظه مالیاتی'
                    helpKey='ui.fiscalMemory'
                    isRequired
                  />
                </label>
                <select
                  className='form-select'
                  aria-label='شناسه حافظه مالیاتی'
                  value={fiscalMemoryId || ''}
                  disabled={isEdit}
                  onChange={(event) => setFiscalMemoryId(Number(event.target.value) || undefined)}
                >
                  <option value=''>انتخاب کنید</option>
                  {fiscalMemories
                    .filter(
                      (token) =>
                        ((token.isActive !== false) === isProductionEnvironment &&
                          token.isEnabled !== false) ||
                        token.id === invoice?.fmi
                    )
                    .map((token) => (
                      <option key={token.id} value={token.id} disabled={token.isEnabled === false}>
                        [{token.isActive === false ? 'سندباکس' : 'عملیاتی'}]{' '}
                        {token.title || token.name || 'حافظه مالیاتی'} — {token.taxid}
                        {token.isDefault ? ' (پیش‌فرض)' : ''}
                      </option>
                    ))}
                </select>
              </div>
              {isEdit ? (
                posPayments.length > 0 && (
                  <div className='col-12 mt-5'>
                    <label className='fw-bold fs-6 mb-2'>پرداخت کارت‌خوان این صورتحساب</label>
                    {posPayments.map((payment, index) => (
                      <div
                        className='rounded border border-dashed border-gray-300 bg-light px-4 py-3 mb-2 text-gray-800'
                        key={payment.id || index}
                      >
                        {numberWithCommas(payment.prc)} ریال
                        {payment.trc && <> - رهگیری {payment.trc}</>}
                        {(payment.terno || payment.terminalNumber) && (
                          <> - پایانه {payment.terno || payment.terminalNumber}</>
                        )}
                        {payment.dt && <> - {payment.dt}</>}
                      </div>
                    ))}
                    <div className='text-gray-600 fs-7'>
                      پرداخت‌های صورتحساب در این فرم تغییر نمی‌کند.
                    </div>
                  </div>
                )
              ) : (
                <div className='col-12 mt-5'>
                  <SearchSelect
                    section='onlinePosTransaction'
                    label='تراکنش کارت‌خوان مرتبط'
                    placeholder='تراکنش آزاد همان روز را جستجو کنید...'
                    stateValue={{value: posTransaction, setValue: setPosTransaction}}
                    extraData={{date, price: priceNumber}}
                    isDisabled={!isNotEmpty(date)}
                    hint='در صورت انتخاب، این تراکنش به عنوان پرداخت صورتحساب ثبت می‌شود و دیگر برای صدورهای بعدی قابل انتخاب نیست. با تغییر تاریخ یا قیمت، تراکنش را دوباره انتخاب کنید.'
                  />
                </div>
              )}
              <div className='col-12 mt-5 rounded border bg-light p-4'>
                <CustomInputs
                  type='toggle'
                  isVertical
                  label='ارسال مستقیم صورتحساب به سامانه مؤدیان پس از ثبت'
                  stateValue={{value: displayedIsSend, setValue: setIsSendFromControl}}
                />
                <div className='text-gray-600 fs-7 mt-2'>
                  مقدار اولیه از تنظیم «فرم وب قیمت تمام‌شده پیشرفته» کسب‌وکار گرفته شده است و تغییر
                  این گزینه فقط روی همین صورتحساب اثر دارد.
                </div>
              </div>
              {!isEdit && isBuyerRequired && !buyer && (
                <div className='col-12 mt-3'>
                  <div className='alert alert-light-warning mb-0'>
                    برای ادامهٔ صدور نوع صورتحساب ۱، خریدار را انتخاب کنید.
                  </div>
                </div>
              )}
            </div>
          )}

          {currentStep == 1 && (
            <div className='border border-dashed border-primary rounded p-8 bg-light-primary'>
              <div className='d-flex justify-content-between py-3 border-bottom border-primary border-opacity-25'>
                <span className='text-gray-600'>
                  <FieldHelpLabel label='شماره صورتحساب' helpKey='header.inno' />
                </span>
                <span className='fw-bolder text-gray-900'>{no}</span>
              </div>
              <div className='d-flex justify-content-between py-3 border-bottom border-primary border-opacity-25'>
                <span className='text-gray-600'>
                  <FieldHelpLabel label='تاریخ صدور' helpKey='header.indatim' />
                </span>
                <span className='fw-bolder text-gray-900'>{date}</span>
              </div>
              <div className='d-flex justify-content-between py-3'>
                <span className='text-gray-600'>
                  <FieldHelpLabel label='قیمت تمام شده' helpKey='body[].fee' />
                </span>
                <span className='fw-bolder text-primary fs-3'>
                  {numberWithCommas(priceNumber)} ریال
                </span>
              </div>
              <div className='d-flex justify-content-between py-3 border-top border-primary border-opacity-25'>
                <span className='text-gray-600'>
                  <FieldHelpLabel label='شناسه حافظه مالیاتی' helpKey='ui.fiscalMemory' />
                </span>
                <span className='fw-bolder text-gray-900'>
                  {fiscalMemories.find((token) => token.id === fiscalMemoryId)?.title ||
                    fiscalMemories.find((token) => token.id === fiscalMemoryId)?.taxid ||
                    'انتخاب نشده'}
                </span>
              </div>
              {showBuyerFields && (
                <div className='d-flex justify-content-between py-3 border-top border-primary border-opacity-25'>
                  <span className='text-gray-600'>
                    <FieldHelpLabel label='خریدار' helpKey='header.tinb' />
                  </span>
                  <span className='fw-bolder text-gray-900 text-start'>
                    {buyer?.label || 'انتخاب نشده'}
                  </span>
                </div>
              )}
              {isInsuranceTemplate(template) && (
                <div className='d-flex justify-content-between py-3 border-top border-primary border-opacity-25'>
                  <span className='text-gray-600'>
                    <FieldHelpLabel label='شناسه یکتای بیمه‌نامه' helpKey='header.in' />
                  </span>
                  <span className='fw-bolder text-gray-900 text-start'>
                    {insPolicyNo}
                    {extInsPolicyNo.trim() && <> - الحاقیه {extInsPolicyNo}</>}
                  </span>
                </div>
              )}
              {posPayments.map((payment, index) => (
                <div
                  className='d-flex justify-content-between py-3 border-top border-primary border-opacity-25'
                  key={payment.id || index}
                >
                  <span className='text-gray-600'>
                    <FieldHelpLabel label='پرداخت کارت‌خوان' helpKey='section.payments' />
                  </span>
                  <span className='fw-bolder text-gray-900 text-start'>
                    {numberWithCommas(payment.prc)} ریال
                    {payment.trc && <> - رهگیری {payment.trc}</>}
                  </span>
                </div>
              ))}
              {posTransaction && (
                <div className='d-flex justify-content-between py-3 border-top border-primary border-opacity-25'>
                  <span className='text-gray-600'>
                    <FieldHelpLabel label='پرداخت کارت‌خوان' helpKey='section.payments' />
                  </span>
                  <span className='fw-bolder text-gray-900 text-start'>{posTransaction.label}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className='mp-invoice-form-actions-spacer'></div>
      <nav className='navbar navbar-light bg-light text-center shadow mp-invoice-form-actions'>
        <div className='container-fluid'>
          <div className='btn-toolbar w-100'>
            <div className='btn-group d-flex justify-content-between w-100' role='group'>
              {currentStep > 0 && (
                <button
                  type='button'
                  className='btn btn-outline-secondary w-10px'
                  onClick={() => setCurrentStep(currentStep - 1)}
                >
                  <div className='d-flex flex-column'>
                    <i className='la la-arrow-right fs-3x'></i>
                    مرحله قبل
                  </div>
                </button>
              )}
              {currentStep == 0 && (
                <button
                  type='button'
                  className='btn btn-outline-secondary flex-grow-1'
                  disabled={!canContinue}
                  onClick={() => setCurrentStep(1)}
                >
                  <div className='d-flex flex-column'>
                    <i className='la la-arrow-left fs-3x'></i>
                    مرحله بعد
                  </div>
                </button>
              )}
              {currentStep == 1 && (
                <button
                  type='button'
                  className='btn btn-primary flex-grow-1 p-0'
                  disabled={loading}
                  onClick={() => save()}
                >
                  <div className='d-flex flex-column'>
                    <i className='las la-save fs-2x'></i>
                    {onTSP ? <>صدور و ارسال صورتحساب</> : <>صدور صورتحساب</>}
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {loading && <ListLoading text='در حال صدور صورتحساب...' />}
      {ebtalNewModal && (
        <Alert
          icon='la-exclamation-triangle'
          type='danger'
          title='توجه'
          body={
            <p className='text-gray-600 fs-4'>
              برای تغییر این اطلاعات، باید صورتحساب را ابطال کنید. برای این کار، از ستون عملیات در
              جدول، گزینه «ابطال» را انتخاب کنید.
            </p>
          }
          cancel={{
            text: 'انصراف',
            fn: () => {
              setEbtalNewModal(false)
            },
          }}
        />
      )}
      {modifyModal && (
        <Alert
          icon='la-info'
          title=''
          body={
            <p className='text-gray-600 fs-4'>
              مطابق دستورالعمل سامانه مؤدیان، تغییراتی که در صورتحساب خود اعمال کرده‌اید منجر به
              صدور <span className='text-gray-800 fw-bolder mx-2'>صورتحساب اصلاحی</span> خواهد شد.
            </p>
          }
          type='warning'
          cancel={{
            text: 'انصراف',
            fn: () => {
              setModifyModal(false)
            },
          }}
          ok={{
            text: 'تایید',
            fn: () => {
              setModifyModal(false)
              save(true)
            },
          }}
        />
      )}
      {errorModal != '' && (
        <Alert
          icon='la-exclamation-triangle'
          title='خطا'
          body={
            <WebInvoiceErrorSupportAction
              errorMessage={errorModal}
              formType={3}
              invoiceType={invoiceType}
              invoiceTemplate={template}
              invoiceNumber={no}
              invoice={invoice}
              appLogId={errorAppLogId}
              onNavigate={() => {
                setErrorModal('')
                setErrorAppLogId(undefined)
              }}
            />
          }
          cancel={{
            text: 'بستن',
            fn: () => {
              setErrorModal('')
              setErrorAppLogId(undefined)
            },
          }}
        />
      )}
    </>
  )
}

export {InvoiceAdvancedCostFormBody}
